export type Language = 'en' | 'nl';

export interface Translations {
  welcome: string;
  whatToDo: string;
  safetyDocument: string;
  reportHistory: string;
  profile: string;
  error: {
    cannotOpenPdf: string;
    failedToOpen: string;
  };
  login: {
    welcome: string;
    instructions: string;
    submitCode: string;
    scanInstructions: string;
    scanQRCode: string;
    scanQRCodeInstructions: string;
    or: string;
    submit: string;
    manualCodePlaceholder: string;
    cameraPermission: {
      title: string;
      message: string;
      ok: string;
    };
    error: {
      invalidCode: string;
      generalError: string;
      emptyCode: string;
      invalidManualCode: string;
    };
  };
  createReport: {
    title: string;
    titleField: string;
    titlePlaceholder: string;
    description: string;
    takePhoto: string;
    choosePhoto: string;
    removePhoto: string;
    submit: string;
    success: string;
    error: {
      noTitle: string;
      noDescription: string;
      cameraPermission: string;
      photoPermission: string;
      uploadError: string;
      createError: string;
      noGroup: string;
    };
    buttons: {
      cancel: string;
      continue: string;
    };
    operationalImpact: {
      label: string;
      placeholder: string;
    };
    temporarySolution: {
      label: string;
      placeholder: string;
    };
    problemType: {
      label: string;
      electrical_safety: string;
      hazardous_materials: string;
      hygiene_health: string;
      environmental: string;
      inspections: string;
      work_equipment: string;
      emergency_response: string;
      working_at_height: string;
      confined_spaces: string;
      psychosocial: string;
      trip_hazard: string;
      ppe: string;
      bhv_safety_round: string;
      near_miss: string;
      unsafe_acting: string;
      other: string;
    };
  };
  location: {
    label: string;
    placeholder: string;
  };
  appManual: {
    title: string;
  };
  common: {
    close: string;
  };
  pwa: {
    installPrompt: string;
    installButton: string;
  };
  pwaInstructions: {
    mainTitle: string;
    subTitle: string;
    ios: {
        title: string;
        step1: string;
        step2: string;
        step3: string;
    };
    android: {
        title: string;
        step1: string;
        step2: string;
        step3: string;
    };
    unknown: {
        title: string;
        message: string;
    };
    footer: string;
  };
}

export const translations: Record<Language, Translations> = {
  en: {
    welcome: 'Welcome',
    whatToDo: 'What would you like to do?',
    safetyDocument: 'Safety Document',
    reportHistory: 'Report History',
    profile: 'Profile',
    error: {
      cannotOpenPdf: 'Cannot open PDF document',
      failedToOpen: 'Failed to open document',
    },
    login: {
      welcome: "Login / Access",
      instructions: "Scan the group QR code or enter the code manually.",
      submitCode: "Login with Code",
      scanInstructions: 'Scan your company QR code to login',
      scanQRCode: 'Scan QR Code',
      scanQRCodeInstructions: 'Point the camera at your company QR code',
      or: 'OR',
      submit: 'Submit',
      manualCodePlaceholder: 'Enter code manually',
      cameraPermission: {
        title: 'Camera Permission',
        message: 'We need camera permission to scan QR codes.',
        ok: 'OK'
      },
      error: {
        invalidCode: 'Invalid QR code. Please try again.',
        generalError: 'Something went wrong. Please try again.',
        emptyCode: 'Please enter a code',
        invalidManualCode: 'Invalid code. Please try again.',
      }
    },
    createReport: {
      title: 'New Report',
      titleField: 'Title',
      titlePlaceholder: 'Short title of the report',
      description: 'Describe the safety issue...',
      takePhoto: 'Take Photo',
      choosePhoto: 'Choose Photo',
      removePhoto: 'Remove Photo',
      submit: 'Submit Report',
      success: 'Report created successfully!',
      error: {
        noTitle: 'Please provide a title for the report.',
        noDescription: 'Please provide a description of the safety issue.',
        cameraPermission: 'Sorry, we need camera access to take photos!',
        photoPermission: 'Sorry, we need photo library access to upload photos!',
        uploadError: 'Cannot upload photo. Would you like to continue without a photo?',
        createError: 'Cannot create report. Please try again.',
        noGroup: 'No group ID found'
      },
      buttons: {
        cancel: 'Cancel',
        continue: 'Continue'
      },
      operationalImpact: {
        label: 'Impact on Operations',
        placeholder: 'Describe the impact on business operations...'
      },
      temporarySolution: {
        label: 'Temporary Solution',
        placeholder: 'Describe any temporary solution applied...'
      },
      problemType: {
        label: 'Type of Safety Problem',
        electrical_safety: 'Electrical Safety',
        hazardous_materials: 'Hazardous Materials',
        hygiene_health: 'Hygiene/Health',
        environmental: 'Environmental',
        inspections: 'Inspections',
        work_equipment: 'Work Equipment',
        emergency_response: 'Emergency Response',
        working_at_height: 'Working at Height',
        confined_spaces: 'Confined Spaces',
        psychosocial: 'Psychosocial Work Stress',
        trip_hazard: 'Trip Hazard',
        ppe: 'PPE',
        bhv_safety_round: 'BHV/Safety Round',
        near_miss: 'Near miss',
        unsafe_acting: 'Unsafe Acting',
        other: 'Other'
      }
    },
    location: {
      label: 'Location',
      placeholder: 'Select location'
    },
    appManual: {
      title: 'Manual'
    },
    common: {
      close: 'Close'
    },
    pwa: {
      installPrompt: 'Install the FerrySafe app on your phone for the best experience',
      installButton: 'Install'
    },
    pwaInstructions: {
      mainTitle: "Install the App",
      subTitle: "For quick access, add this app to your home screen.",
      ios: {
        title: "On iOS (iPhone/iPad)",
        step1: "Tap the 'Share' button in Safari.",
        step2: "Scroll down and tap 'Add to Home Screen'.",
        step3: "Tap 'Add' in the top right corner."
      },
      android: {
        title: "On Android",
        step1: "Tap the menu button (usually 3 dots) in your browser.",
        step2: "Tap 'Install app' or 'Add to Home screen'.",
        step3: "Follow the on-screen prompts."
      },
      unknown: {
          title: "Manual Installation",
          message: "Please use your browser's options to add this page to your home screen or bookmarks."
      },
      footer: "Once installed, you can open the app directly from your home screen."
    }
  },
  nl: {
    welcome: 'Welkom',
    whatToDo: 'Wat wilt u doen?',
    safetyDocument: 'Veiligheidsdocument',
    reportHistory: 'Meldingsgeschiedenis',
    profile: 'Profiel',
    error: {
      cannotOpenPdf: 'Kan PDF document niet openen',
      failedToOpen: 'Kan document niet openen',
    },
    login: {
      welcome: "Inloggen / Toegang",
      instructions: "Scan de groep QR-code of voer de code handmatig in.",
      submitCode: "Inloggen met Code",
      scanInstructions: 'Scan uw bedrijfs-QR-code om in te loggen',
      scanQRCode: 'Scan QR-code',
      scanQRCodeInstructions: 'Richt de camera op uw bedrijfs-QR-code',
      or: 'OF',
      submit: 'Verstuur',
      manualCodePlaceholder: 'Voer code handmatig in',
      cameraPermission: {
        title: 'Camera Toegang',
        message: 'We hebben toegang tot de camera nodig om QR-codes te scannen.',
        ok: 'OK'
      },
      error: {
        invalidCode: 'Ongeldige QR-code. Probeer het opnieuw.',
        generalError: 'Er is iets misgegaan. Probeer het opnieuw.',
        emptyCode: 'Voer een code in',
        invalidManualCode: 'Ongeldige code. Probeer het opnieuw.',
      }
    },
    createReport: {
      title: 'Nieuwe Melding',
      titleField: 'Titel',
      titlePlaceholder: 'Korte titel van de melding',
      description: 'Beschrijf het veiligheidsprobleem...',
      takePhoto: 'Foto Maken',
      choosePhoto: 'Foto Kiezen',
      removePhoto: 'Foto Verwijderen',
      submit: 'Melding Versturen',
      success: 'Melding succesvol aangemaakt!',
      error: {
        noTitle: 'Voeg een titel toe aan de melding.',
        noDescription: 'Geef een beschrijving van het veiligheidsprobleem.',
        cameraPermission: 'Sorry, we hebben toegang tot de camera nodig om fotos te maken!',
        photoPermission: 'Sorry, we hebben toegang tot de fotobibliotheek nodig om fotos te uploaden!',
        uploadError: 'Kan de foto niet uploaden. Wil je doorgaan zonder foto?',
        createError: 'Kan de melding niet aanmaken. Probeer het opnieuw.',
        noGroup: 'Geen groeps-ID gevonden'
      },
      buttons: {
        cancel: 'Annuleren',
        continue: 'Doorgaan'
      },
      operationalImpact: {
        label: 'Impact op operaties',
        placeholder: 'Beschrijf de impact op bedrijfsactiviteiten...'
      },
      temporarySolution: {
        label: 'Tijdelijke oplossing',
        placeholder: 'Beschrijf eventuele tijdelijke oplossing...'
      },
      problemType: {
        label: 'Type veiligheidsprobleem',
        electrical_safety: 'Elektrische veiligheid',
        hazardous_materials: 'Gevaarlijke stoffen',
        hygiene_health: 'Hygiëne/gezondheid',
        environmental: 'Milieu',
        inspections: 'Keuringen',
        work_equipment: 'Arbeidsmiddelen',
        emergency_response: 'Bedrijfsnoodgevallen',
        working_at_height: 'Werken op hoogte',
        confined_spaces: 'Werken in besloten ruimte',
        psychosocial: 'Psychische Sociale Arbeidsbelasting',
        trip_hazard: 'Struikelgevaar',
        ppe: 'PBM\'s',
        bhv_safety_round: 'BHV/Veiligheidsronde',
        near_miss: 'Near miss / Bijna fout',
        unsafe_acting: 'Onveilig gedrag',
        other: 'Anders'
      }
    },
    location: {
      label: 'Locatie',
      placeholder: 'Selecteer locatie'
    },
    appManual: {
      title: 'Handleiding'
    },
    common: {
      close: 'Sluiten'
    },
    pwa: {
      installPrompt: 'Installeer de FerrySafe app op uw telefoon voor de beste ervaring',
      installButton: 'Installeren'
    },
    pwaInstructions: {
      mainTitle: "Installeer de App",
      subTitle: "Voeg deze app toe aan uw startscherm voor snelle toegang.",
      ios: {
        title: "Op iOS (iPhone/iPad)",
        step1: "Tik op de 'Deel'-knop in Safari.",
        step2: "Scroll naar beneden en tik op 'Zet op beginscherm'.",
        step3: "Tik op 'Voeg toe' in de rechterbovenhoek."
      },
      android: {
        title: "Op Android",
        step1: "Tik op de menuknop (meestal 3 stippen) in uw browser.",
        step2: "Tik op 'App installeren' of 'Toevoegen aan startscherm'.",
        step3: "Volg de instructies op het scherm."
      },
      unknown: {
          title: "Handmatige Installatie",
          message: "Gebruik de opties van uw browser om deze pagina aan uw startscherm of bladwijzers toe te voegen."
      },
      footer: "Eenmaal geïnstalleerd, kunt u de app direct vanaf uw startscherm openen."
    }
  }
}; 